import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { rxnormAPI } from '../services/api';

function RxNormDetail() {
  const { rxcui } = useParams();
  const navigate = useNavigate();
  const [drug, setDrug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDrugDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await rxnormAPI.getDrug(rxcui);
        if (response.data) {
          setDrug(response.data);
        } else {
          setError('Drug not found');
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    if (rxcui) {
      fetchDrugDetails();
    }
  }, [rxcui]);

  if (loading) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading drug information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px 20px' }}>
        <div style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          padding: '20px',
          color: '#c33'
        }}>
          <h3 style={{ margin: '0 0 10px 0' }}>Error Loading Drug</h3>
          <p style={{ margin: 0 }}>{error}</p>
        </div>
        <button
          onClick={() => navigate('/rxnorm')}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          ← Back to RxNorm Search
        </button>
      </div>
    );
  }

  if (!drug) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Drug not found</div>
      </div>
    );
  }

  const getSeverityColor = (severity) => {
    const colors = {
      'high': '#dc3545',
      'moderate': '#fd7e14',
      'low': '#ffc107',
      'N/A': '#6c757d'
    };
    return colors[severity] || colors['N/A'];
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <button
        onClick={() => navigate('/rxnorm')}
        style={{
          marginBottom: '20px',
          padding: '8px 16px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          color: '#495057'
        }}
      >
        ← Back to RxNorm Search
      </button>

      {/* Drug Header Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        marginBottom: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        border: '1px solid #e9ecef',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <h1 style={{
          fontSize: '28px',
          marginBottom: '15px',
          color: 'white',
          fontWeight: '700',
          lineHeight: '1.4',
          textShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {drug.name || 'Unknown Drug'}
        </h1>

        {drug.synonym && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '15px'
          }}>
            <span style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.9)',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              💊 Common Name:
            </span>
            <span style={{
              fontSize: '18px',
              color: 'white',
              fontWeight: '600',
              backgroundColor: 'rgba(255,255,255,0.15)',
              padding: '6px 16px',
              borderRadius: '20px',
              backdropFilter: 'blur(10px)'
            }}>
              {drug.synonym}
            </span>
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            padding: '8px 14px',
            borderRadius: '8px',
            backdropFilter: 'blur(10px)'
          }}>
            <span style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.9)',
              fontWeight: '500'
            }}>
              RxCUI:
            </span>
            <span style={{
              fontSize: '15px',
              color: 'white',
              fontWeight: '700',
              fontFamily: 'monospace'
            }}>
              {drug.rxcui}
            </span>
          </div>

          {drug.tty && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              padding: '8px 14px',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <span style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                fontWeight: '500'
              }}>
                Type:
              </span>
              <span style={{
                fontSize: '15px',
                color: 'white',
                fontWeight: '700',
                fontFamily: 'monospace'
              }}>
                {drug.tty}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Drug Identifiers */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e9ecef'
      }}>
        <h2 style={{
          fontSize: '20px',
          marginBottom: '15px',
          color: '#495057',
          fontWeight: '600',
          borderBottom: '2px solid #007bff',
          paddingBottom: '8px',
          background: 'linear-gradient(to right, #007bff, #0056b3)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          🔬 Drug Identifiers
        </h2>
        <div style={{ display: 'table', width: '100%' }}>
          <div style={{
            display: 'table-row',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
            <div style={{
              display: 'table-cell',
              padding: '12px 15px',
              fontWeight: '600',
              color: '#495057',
              width: '200px',
              borderBottom: '1px solid #e9ecef'
            }}>
              📋 RxCUI
            </div>
            <div style={{
              display: 'table-cell',
              padding: '12px 15px',
              color: '#212529',
              borderBottom: '1px solid #e9ecef'
            }}>
              <span style={{
                display: 'inline-block',
                backgroundColor: '#007bff',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {drug.rxcui}
              </span>
            </div>
          </div>

          {drug.tty && (
            <div style={{
              display: 'table-row',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
              <div style={{
                display: 'table-cell',
                padding: '12px 15px',
                fontWeight: '600',
                color: '#495057',
                width: '200px',
                borderBottom: '1px solid #e9ecef'
              }}>
                🏷️ Term Type
              </div>
              <div style={{
                display: 'table-cell',
                padding: '12px 15px',
                color: '#212529',
                borderBottom: '1px solid #e9ecef'
              }}>
                <span style={{
                  display: 'inline-block',
                  backgroundColor: '#28a745',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {drug.tty}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Active Ingredients */}
      {drug.related?.ingredients && drug.related.ingredients.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <h2 style={{
            fontSize: '20px',
            marginBottom: '15px',
            color: '#495057',
            fontWeight: '600',
            borderBottom: '2px solid #28a745',
            paddingBottom: '8px',
            background: 'linear-gradient(to right, #28a745, #1e7e34)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            💊 Active Ingredients
          </h2>
          <div style={{ paddingTop: '10px' }}>
            {drug.related.ingredients.map((ingredient, index) => (
              <div
                key={index}
                style={{
                  padding: '12px 15px',
                  borderBottom: index < drug.related.ingredients.length - 1 ? '1px solid #e9ecef' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ color: '#212529', fontSize: '15px' }}>
                  {ingredient.name}
                </span>
                <a
                  href={`/rxnorm/${ingredient.rxcui}`}
                  style={{
                    color: '#007bff',
                    textDecoration: 'none',
                    fontSize: '14px',
                    padding: '4px 10px',
                    backgroundColor: '#e7f3ff',
                    borderRadius: '4px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#007bff';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#e7f3ff';
                    e.currentTarget.style.color = '#007bff';
                  }}
                >
                  RxCUI: {ingredient.rxcui}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Brand Names */}
      {drug.related?.brand_names && drug.related.brand_names.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <h2 style={{
            fontSize: '20px',
            marginBottom: '15px',
            color: '#495057',
            fontWeight: '600',
            borderBottom: '2px solid #dc3545',
            paddingBottom: '8px',
            background: 'linear-gradient(to right, #dc3545, #bd2130)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            🏷️ Brand Names
          </h2>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            paddingTop: '10px'
          }}>
            {drug.related.brand_names.map((brand, index) => (
              <a
                key={index}
                href={`/rxnorm/${brand.rxcui}`}
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '20px',
                  color: '#856404',
                  textDecoration: 'none',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffc107';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff3cd';
                  e.currentTarget.style.color = '#856404';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {brand.name} ({brand.rxcui})
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Generic Names */}
      {drug.related?.generic_names && drug.related.generic_names.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <h2 style={{
            fontSize: '20px',
            marginBottom: '15px',
            color: '#495057',
            fontWeight: '600',
            borderBottom: '2px solid #17a2b8',
            paddingBottom: '8px',
            background: 'linear-gradient(to right, #17a2b8, #117a8b)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            📋 Generic Names
          </h2>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            paddingTop: '10px'
          }}>
            {drug.related.generic_names.map((generic, index) => (
              <a
                key={index}
                href={`/rxnorm/${generic.rxcui}`}
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  backgroundColor: '#d1ecf1',
                  border: '1px solid #17a2b8',
                  borderRadius: '20px',
                  color: '#0c5460',
                  textDecoration: 'none',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#17a2b8';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#d1ecf1';
                  e.currentTarget.style.color = '#0c5460';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {generic.name} ({generic.rxcui})
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Drug Components */}
      {drug.related?.drug_components && drug.related.drug_components.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <h2 style={{
            fontSize: '20px',
            marginBottom: '15px',
            color: '#495057',
            fontWeight: '600',
            borderBottom: '2px solid #6f42c1',
            paddingBottom: '8px',
            background: 'linear-gradient(to right, #6f42c1, #5a32a3)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            🧪 Drug Components
          </h2>
          <div style={{ paddingTop: '10px' }}>
            {drug.related.drug_components.map((component, index) => (
              <div
                key={index}
                style={{
                  padding: '12px 15px',
                  borderBottom: index < drug.related.drug_components.length - 1 ? '1px solid #e9ecef' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ color: '#212529', fontSize: '15px' }}>
                  {component.name}
                </span>
                <a
                  href={`/rxnorm/${component.rxcui}`}
                  style={{
                    color: '#6f42c1',
                    textDecoration: 'none',
                    fontSize: '14px',
                    padding: '4px 10px',
                    backgroundColor: '#f3e5ff',
                    borderRadius: '4px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#6f42c1';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3e5ff';
                    e.currentTarget.style.color = '#6f42c1';
                  }}
                >
                  RxCUI: {component.rxcui}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Clinical Drugs */}
      {drug.related?.clinical_drugs && drug.related.clinical_drugs.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <h2 style={{
            fontSize: '20px',
            marginBottom: '15px',
            color: '#495057',
            fontWeight: '600',
            borderBottom: '2px solid #20c997',
            paddingBottom: '8px',
            background: 'linear-gradient(to right, #20c997, #1aa179)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            💉 Clinical Drugs
          </h2>
          <div style={{ paddingTop: '10px' }}>
            {drug.related.clinical_drugs.map((clinicalDrug, index) => (
              <div
                key={index}
                style={{
                  padding: '12px 15px',
                  borderBottom: index < drug.related.clinical_drugs.length - 1 ? '1px solid #e9ecef' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ color: '#212529', fontSize: '15px' }}>
                  {clinicalDrug.name}
                </span>
                <a
                  href={`/rxnorm/${clinicalDrug.rxcui}`}
                  style={{
                    color: '#20c997',
                    textDecoration: 'none',
                    fontSize: '14px',
                    padding: '4px 10px',
                    backgroundColor: '#d4f5ed',
                    borderRadius: '4px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#20c997';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#d4f5ed';
                    e.currentTarget.style.color = '#20c997';
                  }}
                >
                  RxCUI: {clinicalDrug.rxcui}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NDC Codes */}
      {drug.ndcs && drug.ndcs.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <h2 style={{
            fontSize: '20px',
            marginBottom: '15px',
            color: '#495057',
            fontWeight: '600',
            borderBottom: '2px solid #fd7e14',
            paddingBottom: '8px',
            background: 'linear-gradient(to right, #fd7e14, #dc6502)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            🔢 NDC Codes {drug.total_ndcs > drug.ndcs.length && `(Showing ${drug.ndcs.length} of ${drug.total_ndcs})`}
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '12px',
            paddingTop: '10px'
          }}>
            {drug.ndcs.map((ndc, index) => (
              <div
                key={index}
                style={{
                  padding: '10px 14px',
                  backgroundColor: '#fff5e6',
                  border: '1px solid #fd7e14',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  color: '#dc6502',
                  textAlign: 'center',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fd7e14';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff5e6';
                  e.currentTarget.style.color = '#dc6502';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {ndc}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Drug Interactions */}
      {drug.interactions && drug.interactions.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <h2 style={{
            fontSize: '20px',
            marginBottom: '15px',
            color: '#495057',
            fontWeight: '600',
            borderBottom: '2px solid #dc3545',
            paddingBottom: '8px',
            background: 'linear-gradient(to right, #dc3545, #bd2130)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            ⚠️ Drug Interactions {drug.interaction_count > 0 && `(${drug.interaction_count} found)`}
          </h2>
          <div style={{ paddingTop: '10px' }}>
            {drug.interactions.slice(0, 10).map((interaction, index) => (
              <div
                key={index}
                style={{
                  marginBottom: '15px',
                  padding: '15px',
                  backgroundColor: '#fff5f5',
                  border: '1px solid #fed7d7',
                  borderRadius: '6px',
                  borderLeft: `4px solid ${getSeverityColor(interaction.severity)}`
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <strong style={{ color: '#212529', fontSize: '15px' }}>
                    {interaction.interactionDrug?.[0]?.name || interaction.drug}
                  </strong>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    backgroundColor: getSeverityColor(interaction.severity),
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    {interaction.severity}
                  </span>
                </div>
                <p style={{
                  margin: '8px 0 0 0',
                  color: '#495057',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {interaction.description}
                </p>
              </div>
            ))}
            {drug.interaction_count > 10 && (
              <div style={{
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                textAlign: 'center',
                color: '#6c757d',
                fontSize: '14px'
              }}>
                Showing 10 of {drug.interaction_count} interactions
              </div>
            )}
          </div>
        </div>
      )}

      {/* No Interactions Message */}
      {(!drug.interactions || drug.interactions.length === 0) && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <h2 style={{
            fontSize: '20px',
            marginBottom: '15px',
            color: '#495057',
            fontWeight: '600'
          }}>
            ⚠️ Drug Interactions
          </h2>
          <p style={{ color: '#28a745', fontSize: '15px', margin: 0 }}>
            ✓ No known drug interactions found
          </p>
        </div>
      )}

      {/* Source Information */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e9ecef'
      }}>
        <h2 style={{
          fontSize: '20px',
          marginBottom: '15px',
          color: '#495057',
          fontWeight: '600',
          borderBottom: '2px solid #6c757d',
          paddingBottom: '8px',
          background: 'linear-gradient(to right, #6c757d, #5a6268)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          🔗 Source Information
        </h2>
        <a
          href={`https://mor.nlm.nih.gov/RxNav/search?searchBy=RXCUI&searchTerm=${drug.rxcui}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            backgroundColor: '#007bff',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '500',
            transition: 'all 0.2s',
            marginTop: '10px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#0056b3';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,123,255,0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#007bff';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          View on RxNav
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z"/>
            <path d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z"/>
          </svg>
        </a>
      </div>
    </div>
  );
}

export default RxNormDetail;
